import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { addToCart as apiAddToCart, } from "../../../ProductDetail/service/productcart.service";
import { getCustomers, } from "../../../Cart/service/Customers.service";
import { persistCartContext, } from "../../../Checkout/service/cart.service";
import { addToCart, setCartCount, } from "../../../Cart/redux/cartSlice";
import { addToWishlist, fetchWishlist, } from "../../../Pages/wishlist/redux/wishlistSlice";
import { getCartCount, } from "../../../../Component/header/service/CartCount.service";
import { getProductId, getProductListingId, getStock, getSizeName } from "./qrcart.helpers";

const useQRCartSection = ({
  product,
  onCartAdded,
}) => {
  const dispatch = useDispatch();

  /* =====================================================
     SIZES
  ===================================================== */

  const sizes = useMemo(() => {
    return Array.isArray(product?.sizes)
      ? product.sizes
      : [];
  }, [product]);


  /* =====================================================
     STATE
  ===================================================== */

  const [selectedSizeId, setSelectedSizeId] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [customQuantity, setCustomQuantity] = useState({});
  const [loadingId, setLoadingId] = useState(null);
  const [wishlistLoadingId, setWishlistLoadingId] = useState(null);
  const [activeCartButtons, setActiveCartButtons] = useState({});
  const [activeWishlistButtons, setActiveWishlistButtons] = useState({});
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [pendingCartItems, setPendingCartItems] = useState([]);


  /* =====================================================
     INITIALIZE
  ===================================================== */

  useEffect(() => {
    if (!sizes.length) {
      setSelectedSizeId(null);
      setQuantities({});
      setCustomQuantity({});
      setActiveCartButtons({});
      setActiveWishlistButtons({});
      setPendingCartItems([]);
      return;
    }

    const firstId = getProductId(
      sizes[0],
      0
    );

    setSelectedSizeId(firstId);

    const initialQuantities = {};

    sizes.forEach((item, index) => {
      const id = getProductId(
        item,
        index
      );

      initialQuantities[id] = 0;
    });

    setQuantities(initialQuantities);
    setCustomQuantity({});
    setActiveCartButtons({});
    setActiveWishlistButtons({});
    setPendingCartItems([]);
  }, [sizes]);


  /* =====================================================
     SELECTED ITEM
  ===================================================== */

  const selectedItem = useMemo(() => {
    if (!sizes.length) {
      return null;
    }

    const found = sizes.find(
      (item, index) =>
        String(
          getProductId(item, index)
        ) === String(selectedSizeId)
    );

    return found || sizes[0];
  }, [
    sizes,
    selectedSizeId,
  ]);


  /* =====================================================
     SELECTED INDEX
  ===================================================== */

  const selectedIndex = useMemo(() => {
    if (!selectedItem) {
      return 0;
    }

    const selectedId = getProductId(
      selectedItem,
      0
    );

    const index = sizes.findIndex(
      (item, i) =>
        String(
          getProductId(item, i)
        ) === String(selectedId)
    );

    return index >= 0 ? index : 0;
  }, [
    sizes,
    selectedItem,
  ]);


  /* =====================================================
     CUSTOMER
  ===================================================== */

  const getSavedCustomer = () => {
    try {
      const saved =
        localStorage.getItem(
          "selectedCustomer"
        );

      return saved
        ? JSON.parse(saved)
        : null;
    } catch {
      return null;
    }
  };


  const loadCustomers = async (
    search = ""
  ) => {
    try {
      const response =
        await getCustomers(search);

      const data =
        Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];

      setCustomers(data);
    } catch (error) {
      console.error(
        "Customer fetch error:",
        error
      );

      setCustomers([]);

      toast.error(
        "Unable to load customers"
      );
    }
  };


  useEffect(() => {
    if (!showCustomerModal) {
      return;
    }

    loadCustomers(customerSearch);
  }, [
    showCustomerModal,
    customerSearch,
  ]);


  /* =====================================================
     CART COUNT
  ===================================================== */

  const refreshCartCount = async (
    customerId = null
  ) => {
    try {
      const response =
        await getCartCount(
          "fresh",
          1,
          customerId
        );

      const count =
        Number(
          response?.data?.cart_count ??
          response?.data?.count ??
          response?.cart_count ??
          response?.count ??
          response?.data?.data?.cart_count ??
          response?.data?.data?.count ??
          0
        ) || 0;

      dispatch(
        setCartCount(count)
      );

      window.dispatchEvent(
        new CustomEvent(
          "cartUpdated",
          {
            detail: { count },
          }
        )
      );

      return count;
    } catch (error) {
      console.error(
        "Refresh cart count error:",
        error
      );

      return 0;
    }
  };


  /* =====================================================
     QUANTITY
  ===================================================== */

  const getQuantity = (
    item,
    index
  ) => {
    const id = getProductId(
      item,
      index
    );

    const qty = Number(
      quantities[id] ?? 0
    );

    return Number.isFinite(qty) && qty >= 0
      ? qty
      : 0;
  };


  const handleQuantityChange = (
    item,
    index,
    value
  ) => {
    const id = getProductId(
      item,
      index
    );

    const stock = getStock(item);
    const qty = Number(value);

    if (!Number.isFinite(qty) || qty < 0) {
      return;
    }

    if (qty > stock) {
      toast.error(
        `Only ${stock} quantity available`
      );
      return;
    }

    setQuantities((prev) => ({
      ...prev,
      [id]: qty,
    }));

    setCustomQuantity((prev) => {
      const next = {
        ...prev,
      };

      delete next[id];

      return next;
    });

    setActiveCartButtons((prev) => ({
      ...prev,
      [id]: false,
    }));
  };


  /* =====================================================
     CUSTOM QUANTITY
  ===================================================== */

  const handleCustomQuantity = (
    item,
    index,
    value
  ) => {
    const id = getProductId(
      item,
      index
    );

    const stock = getStock(item);

    const onlyDigits =
      String(value).replace(
        /\D/g,
        ""
      );

    if (onlyDigits === "") {
      setCustomQuantity((prev) => ({
        ...prev,
        [id]: "",
      }));

      setQuantities((prev) => ({
        ...prev,
        [id]: 0,
      }));

      return;
    }

    const qty = Number(
      onlyDigits
    );

    if (qty <= 0) {
      return;
    }

    if (qty > stock) {
      toast.error(
        `Only ${stock} quantity available`
      );

      setCustomQuantity((prev) => ({
        ...prev,
        [id]: onlyDigits,
      }));

      return;
    }

    setCustomQuantity((prev) => ({
      ...prev,
      [id]: onlyDigits,
    }));

    setQuantities((prev) => ({
      ...prev,
      [id]: qty,
    }));

    setActiveCartButtons((prev) => ({
      ...prev,
      [id]: false,
    }));
  };


  /* =====================================================
     OPEN CUSTOMER
  ===================================================== */

  const openCustomerModal = async (
    selectedItems
  ) => {
    if (!selectedItems?.length) {
      toast.error(
        "Please select quantity first"
      );
      return;
    }

    setPendingCartItems(
      selectedItems
    );

    setCustomerSearch("");
    setSelectedCustomer(null);
    setShowCustomerModal(true);

    await loadCustomers("");
  };


  /* =====================================================
     SINGLE CART API
  ===================================================== */

  const addSingleProductToCart = async (
    item,
    quantity,
    customerId
  ) => {
    const productListingId =
      getProductListingId(item);

    if (!productListingId) {
      throw new Error(
        "Product listing ID not found"
      );
    }

    const payload = {
      cart_type: "fresh",
      user_id: 1,
      customer_id: Number(customerId),
      product_listing_id:
        productListingId,
      qty: Number(quantity),
    };

    const response =
      await apiAddToCart(payload);

    if (
      response?.success === false ||
      response?.data?.success === false
    ) {
      throw new Error(
        response?.message ||
        response?.data?.message ||
        "Unable to add product"
      );
    }

    return response;
  };


  /* =====================================================
     BULK CART
  ===================================================== */

  const addMultipleProductsToCart =
    async (
      selectedItems,
      customerId
    ) => {
      const numericCustomerId =
        Number(customerId);

      if (!numericCustomerId) {
        toast.error(
          "Please select customer"
        );
        return;
      }

      if (
        !Array.isArray(selectedItems) ||
        !selectedItems.length
      ) {
        toast.error(
          "Please select quantity first"
        );
        return;
      }

      try {
        setLoadingId("bulk");

        const customer =
          customers.find(
            (customerItem) =>
              Number(
                customerItem?.id ??
                customerItem?.customer_id
              ) ===
              numericCustomerId
          ) ||
          selectedCustomer;

        if (customer) {
          localStorage.setItem(
            "selectedCustomer",
            JSON.stringify(customer)
          );
        }

        persistCartContext({
          cartType: "fresh",
          userId: 1,
          customerId:
            numericCustomerId,
        });

        const successItems = [];
        const failedItems = [];

        for (const selected of selectedItems) {
          const {
            item,
            quantity,
            stock,
          } = selected;

          if (
            !item ||
            quantity <= 0 ||
            stock <= 0 ||
            quantity > stock
          ) {
            continue;
          }

          try {
            await addSingleProductToCart(
              item,
              quantity,
              numericCustomerId
            );

            dispatch(
              addToCart({
                ...item,
                quantity,
                qty: quantity,
              })
            );

            const productId =
              getProductId(
                item,
                selected.index
              );

            setActiveCartButtons(
              (prev) => ({
                ...prev,
                [productId]: true,
              })
            );

            successItems.push(item);
          } catch (error) {
            console.error(
              `Failed to add ${getSizeName(item)}:`,
              error
            );

            failedItems.push({
              item,
              error,
            });
          }
        }

        if (successItems.length > 0) {
          await refreshCartCount(
            numericCustomerId
          );

          window.dispatchEvent(
            new CustomEvent(
              "cartUpdated"
            )
          );
        }

        if (
  successItems.length > 0 &&
  failedItems.length === 0
) {
  toast.success(
    `${successItems.length} added to cart`
  );
} else if (
  successItems.length > 0 &&
  failedItems.length > 0
) {
  toast.success(
    `${successItems.length} added, ${failedItems.length} failed`
  );
} else {
  toast.error(
    "Unable to add products to cart"
  );

  return;
}


        setPendingCartItems([]);
        setShowCustomerModal(false);

        onCartAdded?.(successItems);
      } catch (error) {
        console.error(
          "Bulk add to cart error:",
          error
        );

        toast.error(
          error?.response?.data?.message ||
          error?.message ||
          "Unable to add products to cart"
        );
      } finally {
        setLoadingId(null);
      }
    };


  /* =====================================================
     ADD TO CART
  ===================================================== */

  const handleAddToCart = async () => {
    const selectedItems =
      sizes
        .map((item, index) => {
          const quantity =
            getQuantity(
              item,
              index
            );

          const stock =
            getStock(item);

          return {
            item,
            index,
            productId:
              getProductId(
                item,
                index
              ),
            quantity,
            stock,
          };
        })
        .filter(
          ({
            quantity,
            stock,
          }) =>
            quantity > 0 &&
            stock > 0 &&
            quantity <= stock
        );

    if (!selectedItems.length) {
      toast.error(
        "Please select quantity first"
      );
      return;
    }

    const invalidItem =
      sizes
        .map((item, index) => ({
          item,
          index,
          quantity:
            getQuantity(
              item,
              index
            ),
          stock:
            getStock(item),
        }))
        .find(
          ({
            quantity,
            stock,
          }) =>
            quantity > stock
        );

    if (invalidItem) {
      toast.error(
        `${getSizeName(
          invalidItem.item
        )}: Only ${
          invalidItem.stock
        } quantity available`
      );

      return;
    }

    const savedCustomer =
      getSavedCustomer();

    const savedCustomerId =
      savedCustomer?.id ??
      savedCustomer?.customer_id;

    if (savedCustomerId) {
      await addMultipleProductsToCart(
        selectedItems,
        savedCustomerId
      );

      return;
    }

    try {
      const response =
        await getCartCount(
          "fresh",
          1,
          null
        );

      const existingCustomerId =
        response?.data?.customer_id ??
        response?.customer_id ??
        response?.data?.data?.customer_id;

      if (existingCustomerId) {
        await addMultipleProductsToCart(
          selectedItems,
          existingCustomerId
        );

        return;
      }
    } catch (error) {
      console.warn(
        "Cart customer lookup failed:",
        error
      );
    }

    await openCustomerModal(
      selectedItems
    );
  };


  /* =====================================================
     CUSTOMER SELECT
  ===================================================== */

  const handleCustomerSelect =
    async (customer) => {
      if (!customer) {
        return;
      }

      const customerId =
        customer?.id ??
        customer?.customer_id;

      if (!customerId) {
        toast.error(
          "Customer ID not found"
        );

        return;
      }

      setSelectedCustomer(
        customer
      );

      localStorage.setItem(
        "selectedCustomer",
        JSON.stringify(customer)
      );

      if (pendingCartItems.length > 0) {
        await addMultipleProductsToCart(
          pendingCartItems,
          customerId
        );
      }
    };


  /* =====================================================
     WISHLIST
  ===================================================== */

  const handleWishlist = async (
    item = selectedItem
  ) => {
    if (!item) {
      toast.error(
        "Please select a size"
      );
      return;
    }

    const productListingId =
      getProductListingId(item);

    if (!productListingId) {
      toast.error(
        "Product listing ID not found"
      );
      return;
    }

    const itemId =
      getProductId(item);

    const savedCustomer =
      getSavedCustomer();

    const customerId =
      savedCustomer?.id ??
      savedCustomer?.customer_id ??
      null;

    const payload = {
      user_id: 1,
      customer_id:
        customerId
          ? Number(customerId)
          : null,
      product_listing_id:
        productListingId,
    };

    try {
      setWishlistLoadingId(
        itemId
      );

      await dispatch(
        addToWishlist(payload)
      ).unwrap();

      setActiveWishlistButtons(
        (prev) => ({
          ...prev,
          [itemId]: true,
        })
      );

      toast.success(
        "Added to Wishlist"
      );

      await dispatch(
        fetchWishlist({
          userId: 1,
          customerId:
            customerId
              ? Number(customerId)
              : null,
        })
      ).unwrap();
    } catch (error) {
      console.error(
        "Wishlist error:",
        error
      );

      toast.error(
        error?.message ||
        "Unable to add to wishlist"
      );
    } finally {
      setWishlistLoadingId(
        null
      );
    }
  };


  /* =====================================================
     SELECT SIZE
  ===================================================== */

  const handleSelectSize = (
    item,
    index
  ) => {
    setSelectedSizeId(
      getProductId(
        item,
        index
      )
    );
  };


  /* =====================================================
     UI DERIVED DATA
  ===================================================== */

  const hasAnyValidQuantity =
    sizes.some(
      (item, index) => {
        const stock =
          getStock(item);

        const quantity =
          getQuantity(
            item,
            index
          );

        return (
          stock > 0 &&
          quantity > 0 &&
          quantity <= stock
        );
      }
    );


  /* =====================================================
     RETURN
  ===================================================== */

  return {
    sizes,
    selectedSizeId,
    selectedItem,
    selectedIndex,
    quantities,
    customQuantity,
    loadingId,
    wishlistLoadingId,
    activeCartButtons,
    activeWishlistButtons,
    customers,
    customerSearch,
    setCustomerSearch,
    showCustomerModal,
    setShowCustomerModal,
    selectedCustomer,
    pendingCartItems,
    hasAnyValidQuantity,
    getQuantity,
    handleQuantityChange,
    handleCustomQuantity,
    handleSelectSize,
    handleAddToCart,
    handleWishlist,
    handleCustomerSelect,
  };
};

export default useQRCartSection;
